import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getPaymentService } from '../../../domains/payment';
import NativeBridge from '../../../core/utils/NativeBridge';
import styles from './PaymentResultPage.module.css';

/**
 * 결제 결과 페이지
 * KSPAY에서 결제 완료 후 리다이렉트되는 페이지
 */
const PaymentResultPage = () => {
  const location = useLocation();
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const processResult = async () => {
      try {
        // KSPAY 결과 파라미터 파싱
        const searchParams = new URLSearchParams(location.search);

        const paymentResult = {
          authyn: searchParams.get('authyn') || '',
          trno: searchParams.get('trno') || '',
          trddt: searchParams.get('trddt') || '',
          trdtm: searchParams.get('trdtm') || '',
          amt: searchParams.get('amt') || '',
          authno: searchParams.get('authno') || '',
          msg1: searchParams.get('msg1') || '',
          msg2: searchParams.get('msg2') || '',
          ordno: searchParams.get('ordno') || '',
          isscd: searchParams.get('isscd') || '',
          aqucd: searchParams.get('aqucd') || '',
          result: searchParams.get('result') || '',
          resultcd: searchParams.get('resultcd') || '',
        };

        const isSuccess = paymentResult.authyn === 'O';

        // 백엔드 API 호출
        const paymentService = getPaymentService();
        const apiResponse = await paymentService.kspayCallback(paymentResult);

        const finalResult = {
          type: 'KSPAY_RESULT',
          success: isSuccess,
          ...paymentResult,
          backendResult: apiResponse,
        };

        setResult(finalResult);

        // Flutter로 결과 전달
        sendToFlutter('KSPAY_RESULT', finalResult);
      } catch (err) {
        console.error('결제 결과 처리 오류:', err);
        setError(err.message);

        // 에러도 Flutter로 전달
        sendToFlutter('KSPAY_RESULT', {
          success: false,
          error: err.message,
        });
      } finally {
        setIsLoading(false);
      }
    };

    processResult();
  }, [location]);

  // Flutter로 메시지 전송 (NativeBridge 사용)
  const sendToFlutter = (type, data) => {
    NativeBridge.send(type, data);
  };

  // 홈으로 이동
  const handleGoHome = () => {
    sendToFlutter('NAVIGATE', {
      destination: 'HOME',
      result: result,
    });
  };

  // 주문상세로 이동 (실패 시)
  const handleGoOrderDetail = () => {
    sendToFlutter('NAVIGATE', {
      destination: 'ORDER_DETAIL',
      orderId: result?.backendResult?.data?.order_id,
      result: result,
    });
  };

  // 주문내역으로 이동 (성공 시)
  const handleGoOrderHistory = () => {
    sendToFlutter('NAVIGATE', {
      destination: 'ORDER_HISTORY',
      result: result,
    });
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.spinner} />
        <p className={styles.loadingText}>결제 결과 처리 중...</p>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className={styles.container}>
        <div className={[styles.icon, styles.fail].join(' ')}>✕</div>
        <h2 className={styles.title}>결제 처리 오류</h2>
        <p className={styles.message}>{error}</p>
        <div className={styles.buttonGroup}>
          <button className={styles.button} onClick={handleGoOrderDetail}>
            주문상세로 돌아가기
          </button>
          <button className={styles.buttonSecondary} onClick={handleGoHome}>
            홈으로
          </button>
        </div>
      </div>
    );
  }

  const isSuccess = result?.success;

  return (
    <div className={styles.container}>
      <div className={[styles.icon, isSuccess ? styles.success : styles.fail].join(' ')}>
        {isSuccess ? '✓' : '✕'}
      </div>

      <h2 className={styles.title}>
        {isSuccess ? '결제가 완료되었습니다' : '결제가 실패했습니다'}
      </h2>

      <p className={styles.message}>{isSuccess ? result.msg2 : result.msg1}</p>

      {isSuccess && (
        <div className={styles.details}>
          <div className={styles.detailRow}>
            <span>결제 금액</span>
            <span>{Number(result.amt).toLocaleString()}원</span>
          </div>
          <div className={styles.detailRow}>
            <span>승인 번호</span>
            <span>{result.authno}</span>
          </div>
          <div className={styles.detailRow}>
            <span>거래 일시</span>
            <span>
              {result.trddt?.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}{' '}
              {result.trdtm?.replace(/(\d{2})(\d{2})(\d{2})/, '$1:$2:$3')}
            </span>
          </div>
        </div>
      )}

      {/* 성공 시 버튼 */}
      {isSuccess && (
        <div className={styles.buttonGroup}>
          <button className={styles.button} onClick={handleGoOrderHistory}>
            주문내역 보기
          </button>
          <button className={styles.buttonSecondary} onClick={handleGoHome}>
            홈으로
          </button>
        </div>
      )}

      {/* 실패 시 버튼 */}
      {!isSuccess && (
        <div className={styles.buttonGroup}>
          <button className={styles.button} onClick={handleGoOrderDetail}>
            주문상세로 돌아가기
          </button>
          <button className={styles.buttonSecondary} onClick={handleGoHome}>
            홈으로
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentResultPage;
